import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, Mail, Heart, Share2, Users, ExternalLink, MessageSquareQuote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, SubscribeForm, RichSection } from '../components/Shared';
import { NextStepBand } from '../components/NextStepBand';
import { FACEBOOK_PAGE_URL, FACEBOOK_REVIEWS_URL } from '../lib/social';

const WAYS = [
  { icon: HandHeart, title: 'Volunteer', body: 'Signs, doors, calls, events. Volunteers are the heart of this campaign and every bit helps.', url: '/volunteer', label: 'Volunteer' },
  { icon: Mail, title: 'Contact Steve', body: 'Questions, concerns, or ideas for Ward 8? Steve wants to hear from you directly.', url: '/contact', label: 'Get in Touch' },
  { icon: Heart, title: 'Support PITA', body: 'Help keep the Public Issue Tracker App running and growing for Ward 8.', url: '/support-pita', label: 'Support PITA' },
  { icon: Share2, title: 'Share PITA', body: 'Tell a neighbour. The more people who report, the clearer the pattern.', url: '/support-pita#share', label: 'Share It' },
  { icon: Users, title: 'Supporters', body: 'See the residents standing with Steve, and add your name.', url: '/supporters', label: 'View Supporters' },
  { icon: MessageSquareQuote, title: 'Leave a Review', body: 'Know Steve or have worked with him? Share your experience on the website or on Facebook.', url: '/leave-a-review', label: 'Leave a Review' },
];

export default function GetInvolved() {
  return (
    <div>
      <PageHeader
        pageKey="get-involved"
        kicker="GET INVOLVED"
        title="Ways to help"
        subtitle="Volunteer, share, subscribe, or just say hello. Every bit of help moves Ward 8 forward."
      />
      <RichSection id="get-involved-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="get-involved-ways-section">
        <SectionHeader kicker="PICK ONE" title="Choose how you want to help" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {WAYS.map(({ icon: Icon, title, body, url, label }) => (
            <Card key={title} className="card-lift" data-testid="get-involved-card">
              <CardContent className="p-5 flex flex-col h-full">
                <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#C9A84C]" />
                </div>
                <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed flex-1">{body}</p>
                <Button asChild className="mt-4 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid={`get-involved-button-${url.replace(/[\/#]/g, '')}`}>
                  <Link to={url}>{label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          ))}
          <Card className="bg-[#1a2744] border-0 card-lift" data-testid="get-involved-subscribe-card">
            <CardContent className="p-5">
              <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center mb-3">
                <Mail className="h-5 w-5 text-[#C9A84C]" />
              </div>
              <p className="font-serif font-bold text-white leading-snug">Subscribe for alerts</p>
              <p className="text-sm text-slate-300 mt-1.5 mb-4 leading-relaxed">Campaign updates, meeting reminders, and public alerts for Ward 8.</p>
              <SubscribeForm compact testId="get-involved-subscribe-form" />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Reviews invitation (exact approved wording) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" data-testid="get-involved-reviews-section">
        <div className="bg-[#1a2744] rounded-2xl p-6 sm:p-8 text-white">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-11 w-11 rounded-xl bg-white/10 items-center justify-center shrink-0">
              <MessageSquareQuote className="h-5 w-5 text-[#C9A84C]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]">REVIEWS</p>
              <p className="font-serif font-black text-xl sm:text-2xl leading-snug mt-1" data-testid="get-involved-reviews-heading">Know Steve or have worked with him? Share your experience.</p>
              <p className="text-slate-200 mt-2 leading-relaxed" data-testid="get-involved-reviews-text">Leave a review here on the website, or read what others are saying and add your own review on Facebook.</p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button asChild className="h-12 px-6 bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press justify-center" data-testid="get-involved-website-review-button">
                  <Link to="/leave-a-review">Leave a Website Review <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
                <Button asChild variant="outline" className="h-12 px-6 border-white/60 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="get-involved-facebook-reviews-button">
                  <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer">Read &amp; Leave Facebook Reviews <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                </Button>
              </div>
              <p className="text-xs text-slate-300 mt-3" data-testid="get-involved-reviews-note">A website review is published here after approval. It is not posted to Facebook automatically.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="get-involved-facebook-band">
          <div className="flex-1">
            <p className="font-serif font-black text-[#1a2744] text-xl leading-snug">Follow along on Facebook</p>
            <p className="text-sm text-slate-600 mt-1">Campaign updates, community photos, and Ward 8 news.</p>
          </div>
          <Button asChild variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press shrink-0" data-testid="get-involved-facebook-button">
            <a href={FACEBOOK_PAGE_URL} target="_blank" rel="noopener noreferrer">Facebook Page <ExternalLink className="h-4 w-4 ml-1.5" /></a>
          </Button>
        </div>
      </section>

      {/* One clear next step */}
      <NextStepBand
        title="Ready to help bring accountability you can see?"
        buttonLabel="Join the Campaign"
        to="/volunteer"
        secondaryLabel="Or start by adding your name as a supporter"
        secondaryTo="/supporters"
        testId="get-involved-next-step"
      />
    </div>
  );
}
